// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useEffect, useContext } from 'react';
import './AggregationsTimeline.scss';

import { AuthContext } from '../../context/AuthContext';
import { firestore } from '../../context/FirebaseConfig';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { de } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

import { subscribeToAggregatedVolume } from '../../shared_components/utils/VolumeUtils';
import LoadingIcon from '../LoadingIcon/LoadingIcon';
import { getColorPalette } from '../../utils/colorPaletteUtils';
import { verticalHoverPlugin, getTooltipTitle, getTimeAxisFormat } from './chartUtils';
import { 
    supplierBackgroundPlugin, 
    getSupplierFooterConfig, 
    getSupplierPluginConfig,
    useSupplierTimeframes,
    SupplierLegend
} from './supplierTimelineUtils';

// Register the components needed for Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
);

export default function VolumeTimeline({
    timeframe,
    timeIntervalMinutes = 5,
    showSuppliers = false,
    onTimeSelected,
}) {
    const [volumeData, setVolumeData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { currentUser, suppliers } = useContext(AuthContext);
    const { t } = useTranslation();

    const supplierTimeframes = useSupplierTimeframes(firestore, suppliers, currentUser.company.company, currentUser.device_id, timeframe, showSuppliers);

    const daysDifference = Math.ceil((timeframe.endDate - timeframe.startDate) / (1000 * 60 * 60 * 24));

    useEffect(() => {
        if (!firestore || !currentUser || !timeframe) {
            return;
        }

        const clientId = currentUser.company.company;
        const deviceId = currentUser.device_id;

        if (!clientId || !deviceId) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        const unsubscribe = subscribeToAggregatedVolume(
            firestore,
            clientId,
            deviceId,
            timeframe.startDate,
            timeframe.endDate,
            timeIntervalMinutes,
            (data) => {
                setVolumeData(data);
                setIsLoading(false);
            },
        );

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [currentUser, timeframe.startDate.getTime(), timeframe.endDate.getTime(), timeIntervalMinutes]);

    // Format chart data
    const chartData = {
        datasets: [
            {
                label: 'Volume',
                data: volumeData.map(item => ({
                    x: new Date(item.start_timestamp),
                    y: item.total_volume
                })),
                backgroundColor: getColorPalette(1)[0], // Add transparency
                borderColor: 'rgba(0, 0, 0, 0)',
                barPercentage: 0.8,
                categoryPercentage: 1,
            }
        ]
    };

    // Chart options
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (event, elements) => {
            if (elements.length > 0 && onTimeSelected) {
                const elementIndex = elements[0].index;
                const dataPoint = volumeData[elementIndex];
                if (dataPoint) {
                    const startTime = new Date(dataPoint.start_timestamp).getTime();
                    const endTime = new Date(dataPoint.end_timestamp).getTime();
                    const midTimestamp = new Date((startTime + endTime) / 2);
                    onTimeSelected(midTimestamp);
                }
            }
        },
        plugins: {
            legend: {
                position: 'top',
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'white',
                titleColor: '#333',
                bodyColor: '#666',
                borderColor: '#ddd',
                borderWidth: 1,
                boxPadding: 6,
                boxWidth: 8,
                boxHeight: 8,
                cornerRadius: 4,
                usePointStyle: true,
                padding: 12,
                ...getSupplierFooterConfig(supplierTimeframes),
                callbacks: {
                    title: (tooltipItems) => getTooltipTitle(tooltipItems, timeframe, timeIntervalMinutes),
                    label: (tooltipItem) => {
                        return `Volume: ${tooltipItem.parsed.y.toFixed(2)} m³`;
                    },
                    labelPointStyle: () => {
                        return {
                            pointStyle: 'circle',
                            rotation: 0
                        };
                    },
                    ...getSupplierFooterConfig(supplierTimeframes)
                }
            },
            ...getSupplierPluginConfig(supplierTimeframes),
            verticalHover: {}
        },
        scales: {
            x: {
                type: 'time',
                time: getTimeAxisFormat(timeframe),
                title: {
                    display: false
                },
                grid: {
                    display: false
                },
                adapters: {
                    date: {
                        locale: de,
                    }
                },
                ticks: {
                    maxRotation: 0,
                    autoSkip: true,
                    source: 'data',
                    font: {
                        size: 12
                    }
                }
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: timeIntervalMinutes === 1440 ? 
                        t('timeline_per_unit', { 
                            value: t('timeline_volume'),
                            unit: t('timeline_time_unit_day')
                        }) : 
                        timeIntervalMinutes === 60 ? 
                            t('timeline_per_unit', {
                                value: t('timeline_volume'),
                                unit: t('timeline_time_unit_hour')
                            }) :
                            t('timeline_per_unit', {
                                value: t('timeline_volume'),
                                unit: t('timeline_time_unit_min', { minutes: timeIntervalMinutes })
                            }),
                    font: {
                        size: 14
                    }
                },
                border: {
                    display: false
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                    drawBorder: false,
                    tickLength: 0
                },
                ticks: {
                    padding: 10,
                    font: {
                        size: 12
                    }
                }
            }
        }
    };

    return (
        <div className="aggregations-timeline">
            {isLoading ? (
                <div className="loading"><LoadingIcon color="rgba(0, 0, 0, 0.2)" /></div>
            ) : volumeData.length === 0 ? (
                <div className="no-data">No volume data available for the selected date</div>
            ) : (
                <div className="chart-container">
                    <div className="chart-container-inner">
                        <Bar 
                            data={chartData} 
                            options={options} 
                            plugins={[supplierBackgroundPlugin, verticalHoverPlugin]} 
                        />
                    </div>
                    {showSuppliers && <SupplierLegend supplierTimeframes={supplierTimeframes} />}
                </div>
            )}
        </div>
    );
} 